import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import blogService from '../services/blogService';

const useBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const normalizeBlogs = (payload) => {
    const rows = Array.isArray(payload) ? payload : (payload?.data || []);

    return rows.map((blog) => ({
      ...blog,
      title: blog.title || blog.card_title || blog.details_title || '',
      image: blog.image || blog.card_image_url || blog.details_image_url || '',
      short_description: blog.short_description || blog.card_short_description || blog.details_short_description || '',
      status: blog.status || 'active',
    }));
  };

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await blogService.getBlogs();
      setBlogs(normalizeBlogs(data));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  const addBlog = async (data) => {
    try {
      await blogService.createBlog(data);
      toast.success('Blog created successfully');
      fetchBlogs();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create blog');
      return false;
    }
  };

  const updateBlog = async (id, data) => {
    try {
      await blogService.updateBlog(id, data);
      toast.success('Blog updated successfully');
      fetchBlogs();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update blog');
      return false;
    }
  };

  const deleteBlog = async (id) => {
    try {
      await blogService.deleteBlog(id);
      toast.success('Blog deleted successfully');
      fetchBlogs();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete blog');
      return false;
    }
  };

  const updateBlogStatus = async (id, status) => {
    try {
      await blogService.updateBlogStatus(id, status);
      toast.success('Blog status updated');
      fetchBlogs();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update blog status');
      return false;
    }
  };

  return {
    blogs,
    loading,
    addBlog,
    updateBlog,
    deleteBlog,
    updateBlogStatus,
    refresh: fetchBlogs,
  };
};

export default useBlogs;
